import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Spin, message, Typography,
  Card, Row, Col, Statistic, Empty, Popconfirm, Alert,
} from 'antd';
import {
  PlusOutlined, DollarOutlined, BankOutlined, WalletOutlined,
  WarningOutlined, CheckCircleOutlined, FundOutlined, SafetyOutlined, DeleteOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { fetchAdvancePayments, deleteAdvancePayment } from '../api';
import VoucherPreviewModal from './VoucherPreviewModal';
import ApprovalStatusBadge, { PendingApprovalAlert } from '../../../components/ApprovalStatusBadge';
import { SplitReferenceCell, resolveSplits } from '../../../components/PaymentSplitCells';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ADVANCE_CATEGORIES = {
  LOAN_PAYMENT: { label: 'Loan Payment', color: 'blue' },
  CUSTOMER_TAKE_HOME: { label: 'Take-Home', color: 'green' },
};

const PAYMENT_RECIPIENTS = {
  CUSTOMER: { label: 'Customer', color: 'green' },
  LENDER: { label: 'Lender', color: 'blue' },
};

const AdvancePaymentLedgerTable = ({
  caseId,
  caseData,
  onPaymentAdded,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  // State
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Format amount as whole rupee without decimals
  const formatWholeAmount = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price || 0);

  // Load payments
  const loadPayments = async () => {
    if (!caseId) return;

    setLoading(true);
    try {
      const result = await fetchAdvancePayments(caseId);
      setPayments(result.data || []);
    } catch (error) {
      console.error('Load payments error:', error);
      messageApi.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [caseId]);

  // Handle payment creation success
  const handlePaymentSuccess = (result) => {
    // Refresh payments table
    loadPayments();

    // Notify parent component to refresh case data and vouchers
    onPaymentAdded?.(result);

    messageApi.success('Payment and voucher created successfully');
  };

  // Calculate financial metrics from case data
  const c = caseData || {};
  // Advances close once the settlement figure is locked in — after that the
  // amount owed to the customer is frozen and payouts run through Settlement.
  const isBlocked = ['PAYMENT_SETTLED', 'BILL_GENERATED', 'CLOSED'].includes(c.status)
    || (!!c.settlementFinalizedAt && (c.totalSettlementPaid || 0) > 0);
  const remainingLoan = c.remainingLoanBalance ?? Math.max(0, (c.closureAmount || 0) - (c.totalLoanPaid || 0));
  const approxFinal = parseFloat(((c.estimatedTotalAmount || 0) - (c.processingFeeAmount || 0) + (c.roundingUpAmount || 0)).toFixed(2));
  const takeHomeEligible = approxFinal - (c.closureAmount || 0);
  const remainingTakeHome = Math.max(0, takeHomeEligible - (c.totalCustomerAdvancePaid || 0));

  const handleDelete = async (paymentId) => {
    setDeletingId(paymentId);
    try {
      await deleteAdvancePayment(paymentId);
      messageApi.success('Payment deleted');
      await loadPayments();
      onPaymentAdded?.(); // trigger parent refresh of case data/vouchers
    } catch (error) {
      console.error('Delete payment error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to delete payment');
    } finally {
      setDeletingId(null);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
      width: 120,
      sorter: (a, b) => new Date(a.paymentDate) - new Date(b.paymentDate),
    },
    {
      title: 'Payment ID',
      dataIndex: 'paymentId',
      key: 'paymentId',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, record) => (
        <ApprovalStatusBadge status={record.status} workflowInstance={record.workflowInstance} />
      ),
    },
    {
      title: 'Category',
      dataIndex: 'advanceCategory',
      key: 'advanceCategory',
      render: (category) => {
        const config = ADVANCE_CATEGORIES[category];
        if (config) {
          return <Tag color={config.color}>{config.label}</Tag>;
        }
        // Fallback for old records
        return <Tag>{(category || '').replace(/_/g, ' ')}</Tag>;
      },
      width: 140,
      filters: Object.keys(ADVANCE_CATEGORIES).map(key => ({
        text: ADVANCE_CATEGORIES[key].label,
        value: key,
      })),
      onFilter: (value, record) => record.advanceCategory === value,
    },
    {
      title: 'Recipient',
      dataIndex: 'paymentRecipient',
      key: 'paymentRecipient',
      render: (recipient) => {
        const config = PAYMENT_RECIPIENTS[recipient];
        return config ? (
          <Tag color={config.color}>{config.label}</Tag>
        ) : (
          <Tag>{recipient}</Tag>
        );
      },
      width: 100,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#3f8600' }}>
          {formatWholeAmount(amount)}
        </Text>
      ),
      width: 140,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: 'UTR / Ref.',
      key: 'transactionReference',
      width: 240,
      render: (_, record) => (
        <SplitReferenceCell record={record} splits={resolveSplits(record)} expectedTotal={record.amount} />
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      width: 200,
    },

    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 130,
      render: (_, record) => {
        const isPending = record.status === 'PENDING_TREASURY';
        return (
          <Popconfirm
            title={isPending ? 'Cancel pending payment?' : 'Delete payment?'}
            description={isPending
              ? 'This will cancel the pending approval request.'
              : 'This will also delete any linked vouchers.'}
            okText={isPending ? 'Cancel' : 'Delete'}
            okButtonProps={{ danger: true, loading: deletingId === record.paymentId }}
            onConfirm={() => handleDelete(record.paymentId)}
            disabled={isBlocked}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingId === record.paymentId}
              disabled={isBlocked}
            >
              {isPending ? 'Cancel' : 'Delete'}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(val || 0);

  return (
    <>
      {contextHolder}

      {/* Financial Summary Cards */}
      <Row gutter={[16, 16]} className="mb-3">
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Closure Amount"
              value={c.closureAmount || 0}
              precision={0}
              prefix={<BankOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Total Loan Paid"
              value={c.totalLoanPaid || 0}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Remaining Loan Balance"
              value={remainingLoan}
              precision={0}
              prefix={remainingLoan > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: remainingLoan > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tag
              color={c.status === 'CLOSED' ? 'green' : 'blue'}
              style={{ fontSize: 14, padding: '6px 16px' }}
            >
              {(c.status || '').replace(/_/g, ' ')}
            </Tag>
          </Card>
        </Col>
      </Row>

      {/* Take-Home Summary Cards */}
      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Take-Home Eligible"
              value={takeHomeEligible}
              precision={0}
              prefix={<WalletOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: takeHomeEligible < 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Customer Advance Paid"
              value={c.totalCustomerAdvancePaid || 0}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Remaining Take-Home"
              value={remainingTakeHome}
              precision={0}
              prefix={remainingTakeHome > 0 ? <SafetyOutlined /> : <CheckCircleOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: remainingTakeHome > 0 ? '#fa8c16' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Capital Exposure"
              value={c.capitalExposure || 0}
              precision={2}
              prefix={<FundOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: '#531dab' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Pending approval alert — dynamic approver info */}
      <PendingApprovalAlert payments={payments} />

      {/* Add Payment Button */}
      <Row justify="space-between" align="middle" className="mb-3">
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            Advance Payments
            {payments.length > 0 && <Text type="secondary"> ({payments.length})</Text>}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setVoucherModalOpen(true)}
            disabled={isBlocked}
          >
            Add Payment with Voucher
          </Button>
          {isBlocked && (
            <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
              Payments blocked — case is {c.status?.replace(/_/g, ' ')?.toLowerCase()}.
            </Text>
          )}
        </Col>
      </Row>

      {/* Payments Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={payments.map((p) => ({ ...p, key: p._id }))}
          columns={columns}
          pagination={payments.length > 10 ? { pageSize: 10, showSizeChanger: true } : false}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description="No payments recorded yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Spin>

      {/* Voucher Preview Modal */}
      <VoucherPreviewModal
        open={voucherModalOpen}
        onCancel={() => setVoucherModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        caseId={caseId}
        caseData={caseData}
        remainingLoan={remainingLoan}
        remainingTakeHome={remainingTakeHome}
      />
    </>
  );
};

export default AdvancePaymentLedgerTable;